import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormGroup, FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
    map,
    share,
    takeUntil,
    debounceTime,
    distinctUntilChanged,
} from 'rxjs/operators';
import { ToastButtons, ToastMessageType, ToastService } from '../../../../../../Common/toast';
import { FileTypeDescriptions } from '../../../../../../Constants/FileTypeDescriptions';
import { FileTypes } from '../../../../../../Constants/FileTypes';
import { FormFlowFieldTypes } from '../../../../../../Constants/FormFlowFieldTypes';
import { ListParameters } from '../../../../../../Constants/ListParameters';
import { ListSelectTypes } from '../../../../../../Constants/ListSelectTypes';
import { ListTypes } from '../../../../../../Constants/ListTypes';
import { ValidationRegexPatterns } from '../../../../../../Constants/ValidationRegexPatterns';
import { PermissionViewModel } from "../../../../../../Models/PermissionViewModel";
import { ListViewModel } from '../../../../../../Models/ListViewModel';
import { SimpleFormFlowField } from '../../../../../../Models/SimpleFormFlowField';
import { PermissionsService, TenantService } from '../../../../../../Services';
import { FormFlowService } from '../../../../../../Services/formFlow.service';
import { LinkedFieldsService } from '../../../../../../Services/linkedFields.service';
import { Field, FieldGroup, IStateNode, Module } from '../../../Models';
import { LocationAttribute } from '../../../Models/location-attribute.model';
import { LocationAttributesService } from '../../../Services/location-attributes.service';
import { ModuleService } from '../../../Services/module.service';
import { findRuleReferences } from '../fields/field-rule-references';
import { FeaturePermissionViewModel } from "@app/Models/FeaturePermissionViewModel";
import { MatToolbar } from '@angular/material/toolbar';
import { MatFormField, MatLabel, MatInput, MatHint } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ListTypeComponent } from '../../../../../../Common/controls/list-type/list-type.component';
import { RegexNumberTypeComponent } from '../../../../../../Common/controls/regex-number-type/regex-number-type.component';
import { TranslationPipe } from '../../../../../../Services/translation.pipe';

@Component({
    selector: 'aw-edit-field',
    templateUrl: './edit-field.component.html',
    styleUrls: ['./edit-field.component.less'],
    imports: [MatToolbar, MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatInput, MatCheckbox, MatButton, MatIcon, ListTypeComponent, RegexNumberTypeComponent, MatHint, MatSelectTrigger, AsyncPipe, TranslationPipe]
})
export class EditFieldComponent implements OnInit, OnDestroy {
    @Input('field') fieldGroupAndField: Observable<{
        fieldGroup: FieldGroup;
        field: Field;
    }>;
    @Input('title') sidebarTitle: string;
    @Output() closePanel: EventEmitter<any> = new EventEmitter();

    addFieldSideBarGroupTitle = '';
    editMode = false;
    fieldForm: Field = new Field();
    formGroup: UntypedFormGroup;

    lookupFields: Field[] = [];
    listAttributesForLookup;
    moduleFieldsForLookup;

    parentFields: SimpleFormFlowField[];
    parentField: SimpleFormFlowField;
    AllowLinkingToNewRecord: boolean;

    calculationTypes: any[];
    calculationType: number;

    drivingAttributes: any[];
    drivingAttribute: string;
    showDrivingAttribute = true;

    drivingField: Field;
    drivingFields: Field[] = [];

    isDisabled = false;
    simpleUserListPermissionsFilter = false;

    isArchived = true;
    fieldTypes: string[] = [];
    listType: string;

    listValueType: 'AIRSWEBLIST';
    lists: ListViewModel[];
    listValues: any[];

    listAlias: string;
    matrixNames: string[] = [];

    modules: string[];

    selectedModule: string;
    selectedPermission: PermissionViewModel;
    modulePermissions: FeaturePermissionViewModel[];
    groupableFields: any[];

    get list(): ListViewModel {
        return this.lists.find(list => list.Key === this.listAlias);
    }

    isOfTypes: string[] = [
        ListSelectTypes.SingleDropDown,
        ListSelectTypes.SingleRadios,
        ListSelectTypes.MultiDropDown,
        ListSelectTypes.MultiCheckbox,
        ListSelectTypes.JumboRadio,
    ];

    simpleUserListIsOfTypes: string[] = [
        ListSelectTypes.SingleDropDown,
        ListSelectTypes.MultiDropDown,
    ];

    singleListTypes: ListSelectTypes[] = [
        ListSelectTypes.SingleDropDown,
        ListSelectTypes.SingleRadios,
        ListSelectTypes.JumboRadio,
    ];

    simpleUserListSingleListTypes: ListSelectTypes[] = [
        ListSelectTypes.SingleDropDown
    ];

    isOfType: string;
    fieldFilterName: string;

    moduleId = '';
    version = 1;
    stepId = '';
    stepFields: Field[];
    selectedValue: string;
    stateNodes: IStateNode[];

    listItemsCache: any = {};

    errorString: string;
    fieldTypeError: string;
    relatedToCalcField: boolean;
    editableModules: Module[];
    applicableModuleStatusesError: string;
    simpleUserListErrorMessage: string;

    locationAttributes: LocationAttribute[] = new Array<LocationAttribute>();
    targetHierarchyAttribute: string;

    allFileTypes: FileTypes[] = Object.values(FileTypes)
        .filter((index) => typeof index !== 'number')
        .map((index) => FileTypes[index] as unknown as FileTypes);

    unsignedIntegerInputRestriction =
        ValidationRegexPatterns.UnsignedIntegerInputRestriction;
    signedIntegerInputRestriction =
        ValidationRegexPatterns.SignedIntegerInputRestriction;
    unsignedDecimalInputRestriction =
        ValidationRegexPatterns.UnsignedDecimalInputRestriction;
    signedDecimalInputRestriction =
        ValidationRegexPatterns.SignedDecimalInputRestriction;

    private fieldId$ = new Subject<string>();
    private destroy$ = new Subject<void>();
    private subscriptions: Subscription[] = [];

    get linkedRecordFields(): Field[] {
        return this.stepFields.filter(
            (f) => f.FieldType === FormFlowFieldTypes.LinkedRecord || f.FieldType === FormFlowFieldTypes.MultiLinkedRecord
        );
    }

    get listFields(): Field[] {
        return this.stepFields.filter(
            (f) =>
                f.FieldType === FormFlowFieldTypes.List &&
                f.ListType === ListSelectTypes.SingleDropDown &&
                f.ListValueType === ListTypes.AirswebList
        );
    }

    get allListFields(): Field[] {
        return this.stepFields.filter(
            (f) =>
                f.FieldType === FormFlowFieldTypes.List &&
                f.ListValueType === ListTypes.AirswebList
        );
    }

    constructor(
        private tenantService: TenantService,
        private moduleService: ModuleService,
        private linkedFieldsService: LinkedFieldsService,
        private formFlowService: FormFlowService,
        private activatedRoute: ActivatedRoute,
        private toast: ToastService,
        private locationAttributeService: LocationAttributesService,
        private permissionsService: PermissionsService
    ) { }

    ngOnInit() {
        this.moduleId = this.activatedRoute.snapshot.params.moduleId;
        this.version = this.activatedRoute.snapshot.params.version;
        this.moduleService.getAllowedFieldTypes().then((allowedFieldTypes) => {
            this.fieldTypes = allowedFieldTypes;
        });

        this.moduleService.getModules(this.tenantService.selectedTenant)
            .then(modules => { this.editableModules = modules.filter(module => module.IsEditable); });
        this.subscriptions.push(
            this.tenantService
                .getAddedList$()
                .subscribe((listAlias) => (this.listAlias = listAlias))
        );

        this.tenantService.getGroupableFields(this.moduleId).subscribe(groupablesResponse => {
            this.groupableFields = groupablesResponse.Model?.GroupableFields;
        });

        this.getAllMatricesAndMapMatrixNames();

        this.locationAttributeService
            .getHierarchyAttributes(this.tenantService.selectedTenant)
            .subscribe((result) => {
                this.locationAttributes = result;
            });

        this.subscribeToFieldId();

        this.subscriptions.push(
            combineLatest(
                [this.moduleService.module(),
                this.activatedRoute.queryParams]
            ).subscribe(([module, params]) => {
                this.stepId = params.stepId;
                if (this.stepId) {
                    this.formFlowService
                        .getParentFields(
                            this.tenantService.selectedTenant.TenantId,
                            this.moduleId,
                            this.version,
                            this.stepId
                        )
                        .subscribe((parentFields) => (this.parentFields = parentFields));
                }

                this.tenantService.getLists().subscribe((r) => {
                    this.lists = r;
                });

                this.formFlowService.getCalculationTypes().subscribe((res) => {
                    this.calculationTypes = res;
                });
            })
        );

        this.subscriptions.push(
            this.fieldGroupAndField.subscribe((fieldGroupAndField) => {
                if (fieldGroupAndField.field) {
                    Object.assign(this.fieldForm, fieldGroupAndField.field);
                    this.fieldForm.OriginalFieldId = this.fieldForm.FieldId;

                    this.getFirstDrivingField();
                    this.editMode = true;
                    this.sidebarTitle = 'Edit Field';
                    this.selectedValue = this.fieldForm.FieldType;
                    this.listType = this.fieldForm.ListType;
                    this.listValueType = this.fieldForm.ListValueType;
                    this.isOfType = this.fieldForm.IsOfType;

                    this.calculationType = this.fieldForm.CalculationType;
                    this.parentField = this.fieldForm.DrivingField;
                    this.drivingAttribute = this.fieldForm.DrivingAttribute;
                    this.editMode = true;
                    this.fieldForm.index = this.moduleService.fields.indexOf(
                        this.fieldForm
                    );
                    this.getDrivingAttributes(this.parentField);
                    this.relatedToCalculationField(this.fieldForm.FieldId);

                    this.listAlias = null;
                    this.fieldFilterName = this.fieldForm.FieldFilterName;

                    if (this.fieldForm.ListParams) {
                        this.listAlias =
                            this.fieldForm.ListParams[ListParameters.ListAlias];
                        this.fieldFilterName = this.fieldForm.FieldFilterName =
                            this.fieldForm.ListParams[ListParameters.FieldFilterName];
                        this.targetHierarchyAttribute =
                            this.fieldForm.ListParams[ListParameters.HierarchyAttribute];
                    }

                    if (this.fieldForm.LinkedRecordField) {
                        this.getFieldsForModule(this.fieldForm.LinkedRecordModuleId);
                    }

                    if (this.fieldForm.ListField) {
                        this.getListAttributesForList(this.fieldForm.ListField.ListAlias);
                    }
                } else {
                    this.fieldForm = new Field();
                    this.selectedValue = this.fieldForm.FieldType;
                    this.editMode = false;
                    this.sidebarTitle = 'Add Field';
                    this.fieldTypeValidate();
                    this.moduleFieldsForLookup = null;
                    this.listAttributesForLookup = null;
                }

                this.addFieldSideBarGroupTitle =
                    fieldGroupAndField.fieldGroup.UiGroupName;
                this.fieldForm.GroupName = fieldGroupAndField.fieldGroup.GroupName;
                this.fieldForm.UiGroupName = fieldGroupAndField.fieldGroup.UiGroupName;
                this.lookupFields = this.getLookupFields(
                    this.moduleService.selectedStep.Fields
                );
            })
        );
        this.subscriptions.push(
            this.moduleService.getCurrentStepFields().subscribe((fields) => {
                this.stepFields = fields;
            })
        );

        this.moduleService
            .getModuleStates(
                this.tenantService.selectedTenant,
                this.moduleId,
                this.version
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((stateNodes: IStateNode[]) => {
                this.stateNodes = stateNodes;
            });

        this.tenantService
            .getAllowedModules(
                this.tenantService.selectedTenant.Value
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((moduleIds: string[]) => {
                this.modules = moduleIds;
            });

        this.permissionsService
            .getPermissionsForAllModules(this.tenantService.selectedTenant)
            .pipe(takeUntil(this.destroy$))
            .subscribe((permissions) => {
                this.modulePermissions = permissions;
                this.selectedModule = this.moduleId;
            });
    }

    getLookupFields(fields: Field[]): Field[] {
        let result = [
            ...fields.filter((x) => x.FieldUniqueId === FormFlowFieldTypes.Lookup),
        ];
        const multiFields = fields.filter(
            (x) => x.FieldUniqueId === FormFlowFieldTypes.MultiItem
        );
        if (multiFields.length > 0) {
            for (const field of multiFields) {
                result = [...this.getLookupFields(field.MultiFields)];
            }
        }
        return result;
    }

    getFieldsForModule(moduleId: string) {
        this.moduleFieldsForLookup = this.formFlowService
            .getAllFieldsForModuleLatestVersion(
                this.tenantService.selectedTenant.TenantId,
                moduleId
            )
            .pipe(share());

        return this.moduleFieldsForLookup;
    }

    getPermissionsForModule(moduleId: string): PermissionViewModel[] {
        return this.modulePermissions.find(p => p.FeatureAlias === moduleId).Permissions;
    }

    getListAttributesForList(listAlias: string) {
        this.listAttributesForLookup = this.tenantService
            .getListAttributeDefinitions(listAlias)
            .pipe(share());
        return this.listAttributesForLookup;
    }

    compareOtherModuleField(a: SimpleFormFlowField, b: SimpleFormFlowField) {
        return (
            a.ParentPropertyName === b.ParentPropertyName && a.StepId === b.StepId
        );
    }
    compareLinkedRecordField(a: Field, b: Field) {
        return a.FieldId === b.FieldId;
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.subscriptions.forEach((sub) => sub?.unsubscribe());
    }

    saveField() {
        if (this.isDisabled) {
            return;
        }

        this.isDisabled = true;
        this.fieldForm.FieldId = this.getFieldIdFromFieldNameAndType(
            this.fieldForm
        ) ?? '';

        if (!this.validate('Field Id', this.fieldForm.FieldId) || !this.validationPass()) {
            this.isDisabled = false;
            return;
        }

        switch (this.selectedValue) {
            case FormFlowFieldTypes.List:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.List;
                this.fieldForm.IsOfType = this.isOfType;
                this.fieldForm.ListType = this.isOfType;
                this.fieldForm.ListAlias = this.listAlias;
                this.fieldForm.FieldFilterName = this.fieldFilterName;
                break;
            case FormFlowFieldTypes.Text:
            case FormFlowFieldTypes.Number:
            case FormFlowFieldTypes.Email:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Textbox;
                break;
            case FormFlowFieldTypes.Date:
            case FormFlowFieldTypes.DateTime:
            case FormFlowFieldTypes.DateTimePicker:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.DateTimePicker;
                break;
            case FormFlowFieldTypes.Checkbox:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Checkbox;
                break;
            case FormFlowFieldTypes.GeoLocation:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.GeoLocation;
                break;
            case FormFlowFieldTypes.Cron:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Cron;
                break;
            case FormFlowFieldTypes.Calc:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Calc;
                this.fieldForm.CalculationType = this.calculationType;
                this.fieldForm.DrivingField = this.parentField;
                this.fieldForm.DrivingAttribute = this.drivingAttribute;
                break;
            case FormFlowFieldTypes.File:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.File;
                break;
            case FormFlowFieldTypes.LinkedRecord:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.LinkedRecord;
                break;
            case FormFlowFieldTypes.MultiLinkedRecord:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.MultiLinkedRecord;
                break;
            case FormFlowFieldTypes.Label:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Label;
                break;
            case FormFlowFieldTypes.LinkedFieldTree:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.LinkedFieldTree;
                break;
            case FormFlowFieldTypes.Lookup:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Lookup;
                if (!this.fieldForm.ListParams) {
                    this.fieldForm.ListParams = {} as Record<string, string>;
                }
                this.fieldForm.ListParams[ListParameters.HierarchyAttribute] =
                    this.targetHierarchyAttribute;
                if (this.fieldForm.LinkedRecordField) {
                    this.fieldForm.LinkedRecordFieldId =
                        this.fieldForm.LinkedRecordField.FieldId;
                }
                this.lookupFields.push(this.fieldForm);
                break;
            case FormFlowFieldTypes.SignaturePlaceholderField:
                this.fieldForm.FieldUniqueId =
                    FormFlowFieldTypes.SignaturePlaceholderField;
                break;
            case FormFlowFieldTypes.Escalation:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.Escalation;
                this.fieldForm.IsMandatory = true;
                break;
            case FormFlowFieldTypes.SeverityMatrix:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.SeverityMatrix;
                break;
            case FormFlowFieldTypes.SimpleUserList:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.SimpleUserList;
                this.fieldForm.IsOfType = this.isOfType;
                this.fieldForm.ListType = this.isOfType;
                this.fieldForm.IsMulti = this.isOfType === ListSelectTypes.MultiDropDown;
                this.fieldForm.SimpleUserListPermissionsFilter = this.simpleUserListPermissionsFilter;
                if (this.simpleUserListPermissionsFilter) {
                    this.fieldForm.SimpleUserListSelectedModule = this.selectedModule;
                    this.fieldForm.SimpleUserListSelectedPermission = this.selectedPermission.Name;
                }
                break;
            case FormFlowFieldTypes.ElectronicSignature:
                this.fieldForm.FieldUniqueId = FormFlowFieldTypes.ElectronicSignature;
                break;
        }

        this.fieldForm.FieldType = this.selectedValue;
        this.fieldForm.IsCustomField = true;

        this.moduleService
            .addField(
                this.fieldForm,
                this.tenantService.selectedTenant,
                this.moduleId,
                this.version,
                this.stepId
            )
            .subscribe(() => {
                this.closePanel.emit();
            });
    }

    isTreeControl() {
        return this.selectedValue === FormFlowFieldTypes.LinkedFieldTree;
    }

    addList() {
        this.moduleService.addList();
    }

    fieldRulesEditable(field: Field): boolean {
        return (
            field.FieldId !== this.drivingField.FieldId &&
            !field.IsMandatory &&
            !field.IsAlwaysHidden
        );
    }

    getFirstDrivingField(): void {
        const fieldControl = JSON.parse(this.fieldForm.Json).Control;

        const calculation = fieldControl.Properties.Calculation;

        if (calculation) {
            this.fieldForm.DrivingField =
                calculation.DrivingFields[0].DrivingFieldCollection;
            this.fieldForm.DrivingAttribute =
                calculation.DrivingFields[0].DrivingAttribute;
        }
    }

    getFieldIdFromFieldNameAndType(field: Field): string {
        let fieldId = field.FieldId;

        if (fieldId.endsWith('Aliases')) {
            const idx = fieldId.lastIndexOf('Aliases');
            fieldId = fieldId.substring(0, idx);
        } else if (fieldId.endsWith('Alias')) {
            const idx = fieldId.lastIndexOf('Alias');
            fieldId = fieldId.substring(0, idx);
        }

        if (this.selectedValue === FormFlowFieldTypes.List) {
            switch (this.isOfType) {
                case ListSelectTypes.SingleDropDown:
                case ListSelectTypes.SingleRadios:
                case ListSelectTypes.JumboRadio:
                    fieldId += 'Alias';
                    break;
                case ListSelectTypes.MultiDropDown:
                case ListSelectTypes.MultiCheckbox:
                    fieldId += 'Aliases';
                    break;
            }
        }
        return this.slugify(fieldId);
    }

    getDrivingAttributes(parentField: SimpleFormFlowField): void {
        if (parentField) {
            const field = this.moduleService.getField(
                parentField.StepId,
                parentField.ParentPropertyName
            );

            switch (field.FieldType) {
                case FormFlowFieldTypes.List: {
                    this.showDrivingAttribute = true;
                    this.tenantService
                        .getListAttributeDefinitions(field.ListAlias)
                        .subscribe((res) => {
                            this.drivingAttributes = res;
                        });
                    break;
                }

                case FormFlowFieldTypes.Number: {
                    this.showDrivingAttribute = false;
                    this.drivingAttribute = null;
                }
            }
        }
    }

    getListItems() {
        const cacheVal =
            this.listItemsCache[
            this.drivingField.ListParams[ListParameters.ListAlias]
            ];
        let itemsSource: Observable<any[]>;
        if (cacheVal) {
            itemsSource = new Observable<any>((items: any) => items.next(cacheVal));
        } else {
            itemsSource = this.tenantService
                .getListItems([
                    ListTypes.AirswebList,
                    this.drivingField.ListParams[ListParameters.ListAlias],
                ])
                .pipe(map((r) => r as any[]));
        }
        return itemsSource.pipe(
            map((r) => {
                for (const i of r) {
                    i.shown = true;
                }
                return r;
            }),
            map((items: any) => {
                this.listValues = items;
                this.listItemsCache[
                    this.drivingField.ListParams[ListParameters.ListAlias]
                ] = items;
            })
        );
    }

    slugify(input: string): string {
        if (!input) {
            return null;
        }

        // trim and remove invalid chars
        return input.trim().replace(/[^a-zA-Z0-9_]/g, '');
    }

    onFieldTypeChanged(): boolean {
        if (this.fieldTypeValidate()) {
            if (this.selectedValue === FormFlowFieldTypes.Number) {
                this.fieldForm.RegexRestrict = this.unsignedIntegerInputRestriction;
            } else {
                this.fieldForm.RegexRestrict = null;
            }

            return true;
        }

        return false;
    }

    fieldTypeValidate() {
        if (!this.selectedValue) {
            this.fieldTypeError = 'Please select a field type.';
            return false;
        } else if (
            this.fieldForm.FieldType === FormFlowFieldTypes.List &&
            this.selectedValue !== FormFlowFieldTypes.List &&
            this.relatedToCalcField
        ) {
            this.fieldTypeError = 'Field type is related to a calc field.';
            return false;
        } else {
            this.fieldTypeError = null;
            this.fieldForm.AllowLinkingToNewRecord =
                this.selectedValue === FormFlowFieldTypes.LinkedRecord || this.selectedValue === FormFlowFieldTypes.MultiLinkedRecord;
            return true;
        }
    }

    updateField() {
        Promise.all([
            this.moduleService
                .patchField(
                    this.tenantService.selectedTenant,
                    this.moduleId,
                    this.stepId,
                    this.fieldForm
                )
                .toPromise(),
            this.moduleService
                .updateField(
                    this.fieldForm,
                    this.tenantService.selectedTenant,
                    this.moduleId,
                    this.version,
                    this.stepId
                )
                .toPromise(),
        ]).then(() => this.closePanel.emit());
    }

    compareSimpleFieldViewModels(
        o1: SimpleFormFlowField,
        o2: SimpleFormFlowField
    ): boolean {
        if (
            o1 &&
            o2 &&
            o1.StepId === o2.StepId &&
            o1.ParentPropertyName === o2.ParentPropertyName
        ) {
            return true;
        }

        return false;
    }

    onUpdateFieldClick(): void {
        if (
            !this.escalationApplicableModuleStatusIdsFieldValidation() ||
            !this.linkedFieldMandatoryFieldValidation() ||
            !this.simpleUserListValidation()
        ) {
            return;
        }

        let updatedFieldUniqueId: string;

        switch (this.selectedValue) {
            case FormFlowFieldTypes.List:
                updatedFieldUniqueId = FormFlowFieldTypes.List;
                break;
            case FormFlowFieldTypes.Text:
            case FormFlowFieldTypes.Number:
            case FormFlowFieldTypes.Email:
                updatedFieldUniqueId = FormFlowFieldTypes.Textbox;
                break;
            case FormFlowFieldTypes.Date:
            case FormFlowFieldTypes.DateTime:
            case FormFlowFieldTypes.DateTimePicker:
                updatedFieldUniqueId = FormFlowFieldTypes.DateTimePicker;
                break;
            case FormFlowFieldTypes.Checkbox:
                updatedFieldUniqueId = FormFlowFieldTypes.Checkbox;
                break;
            case FormFlowFieldTypes.GeoLocation:
                updatedFieldUniqueId = FormFlowFieldTypes.GeoLocation;
                break;
            case FormFlowFieldTypes.Cron:
                updatedFieldUniqueId = FormFlowFieldTypes.Cron;
                break;
            case FormFlowFieldTypes.Calc:
                updatedFieldUniqueId = FormFlowFieldTypes.Calc;
                break;
            case FormFlowFieldTypes.File:
                updatedFieldUniqueId = FormFlowFieldTypes.File;
                break;
            case FormFlowFieldTypes.LinkedRecord:
                updatedFieldUniqueId = FormFlowFieldTypes.LinkedRecord;
                break;
            case FormFlowFieldTypes.MultiLinkedRecord:
                updatedFieldUniqueId = FormFlowFieldTypes.MultiLinkedRecord;
                break;
            case FormFlowFieldTypes.Lookup:
                updatedFieldUniqueId = FormFlowFieldTypes.Lookup;
                break;
            case FormFlowFieldTypes.Label:
                updatedFieldUniqueId = FormFlowFieldTypes.Label;
                break;
            case FormFlowFieldTypes.LinkedFieldTree:
                updatedFieldUniqueId = FormFlowFieldTypes.LinkedFieldTree;
                break;
            case FormFlowFieldTypes.Escalation:
                updatedFieldUniqueId = FormFlowFieldTypes.Escalation;
                this.fieldForm.IsMandatory = true;
                break;
            case FormFlowFieldTypes.SeverityMatrix:
                updatedFieldUniqueId = FormFlowFieldTypes.SeverityMatrix;
                break;
            case FormFlowFieldTypes.SimpleUserList:
                updatedFieldUniqueId = FormFlowFieldTypes.SimpleUserList;
                break;
            case FormFlowFieldTypes.ElectronicSignature:
                updatedFieldUniqueId = FormFlowFieldTypes.ElectronicSignature;
                break;
        }

        this.linkedFieldsService
            .getIsFieldPartOfAnyLinkedFieldSets(
                this.tenantService.selectedTenant.TenantId,
                this.moduleId,
                this.version,
                this.fieldForm.FieldId
            )
            .subscribe((r) => {
                if (
                    r &&
                    (this.fieldForm.FieldUniqueId !== updatedFieldUniqueId ||
                        this.fieldForm.ListType !== this.isOfType ||
                        this.fieldForm.ListAlias !== this.listAlias)
                ) {
                    this.toast.sendMessage(
                        'Unable to Update',
                        "This field is part of one or more Linked Field Sets. You must remove the Linked Field Set before updating this field's Field Type, List Alias, or Type of List.",
                        ToastMessageType.error
                    );
                } else if (this.relatedToCalcField) {
                    this.toast.sendMessage(
                        'Unable to update',
                        'This field drives a calculation field. You must remove the link to the calculation before the field type can be updated.',
                        ToastMessageType.error
                    );
                } else {
                    const newFieldId = this.getFieldIdFromFieldNameAndType(this.fieldForm);
                    const originalFieldId = this.fieldForm.OriginalFieldId;
                    const references = this.editMode && originalFieldId && newFieldId !== originalFieldId
                        ? findRuleReferences(this.moduleService.getModuleFields(), { ...this.fieldForm, FieldId: originalFieldId, MultiFields: [] } as Field)
                        : [];
                    if (references.length > 0) {
                        this.toast.sendMessage(
                            'Rules No Longer Apply',
                            `Rules on these fields use this field: ${references.map(f => f.FieldId).join(', ')}. This change renames the field to ${newFieldId}, so those rules stop working, and those fields always show.`,
                            ToastMessageType.warning,
                            ToastButtons.Ok,
                            0
                        );
                    }
                    this.fieldForm.FieldId = newFieldId;
                    this.fieldForm.FieldUniqueId = updatedFieldUniqueId;
                    this.fieldForm.FieldType = this.selectedValue;
                    this.fieldForm.IsCustomField = true;

                    if (this.selectedValue === FormFlowFieldTypes.List) {
                        this.fieldForm.IsOfType = this.isOfType;
                        this.fieldForm.ListType = this.fieldForm.IsOfType;
                        this.fieldForm.ListValueType = this.listValueType;
                        this.fieldForm.ListAlias = this.listAlias;
                        this.fieldForm.FieldFilterName = this.fieldFilterName;
                    } else if (this.selectedValue === FormFlowFieldTypes.Calc) {
                        this.fieldForm.CalculationType = this.calculationType;
                        this.fieldForm.DrivingAttribute = this.drivingAttribute;
                        this.fieldForm.DrivingField = this.parentField;
                    }

                    this.updateField();
                }
            });
    }

    relatedToCalculationField(fieldId: string) {
        this.formFlowService
            .IsFieldLinkedAnotherOfType(
                fieldId,
                this.tenantService.selectedTenant.TenantId,
                this.moduleId,
                this.version,
                FormFlowFieldTypes.Calc
            )
            .subscribe((result) => {
                if (result !== undefined) {
                    this.relatedToCalcField = result;
                } else {
                    this.relatedToCalcField = false;
                }
            });
    }

    disableManageListItems(): boolean {
        return !this.list;
    }

    manageListItems(): void {
        this.moduleService.editList(this.list.Key, this.listAlias);
    }

    validationPass(): boolean {
        if (
            this.fieldForm.FieldId &&
            (this.fieldForm.FieldName ||
                this.selectedValue === FormFlowFieldTypes.Label)
        ) {
            return (
                !this.errorString &&
                !this.fieldTypeError &&
                this.numberMandatoryFieldsValidation() &&
                this.calculationMandatoryFieldsValidation() &&
                this.linkedFieldMandatoryFieldValidation() &&
                this.listMandatoryFieldValidation() &&
                this.escalationApplicableModuleStatusIdsFieldValidation() &&
                this.simpleUserListValidation()
            );
        }
        return false;
    }

    numberMandatoryFieldsValidation(): boolean {
        return (
            this.selectedValue !== FormFlowFieldTypes.Number ||
            !!this.fieldForm.RegexRestrict
        );
    }

    calculationMandatoryFieldsValidation(): boolean {
        if (
            this.selectedValue === FormFlowFieldTypes.Calc &&
            (!this.calculationType ||
                (!this.drivingAttribute && this.showDrivingAttribute) ||
                !this.parentField)
        ) {
            return false;
        }

        return true;
    }

    linkedFieldMandatoryFieldValidation(): boolean {
        if (this.selectedValue !== FormFlowFieldTypes.LinkedRecord && this.selectedValue !== FormFlowFieldTypes.MultiLinkedRecord) {
            return true;
        }

        if (
            this.fieldForm.LinkedRecordModuleId === undefined ||
            this.fieldForm.LinkedRecordModuleId === null
        ) {
            return false;
        }

        if (
            !this.fieldForm.AllowLinkingToNewRecord &&
            !this.fieldForm.AllowLinkingToExistingRecords
        ) {
            return false;
        }
        return true;
    }

    listMandatoryFieldValidation() {
        if (this.selectedValue !== FormFlowFieldTypes.List) {
            return true;
        }

        if (
            this.listAlias === undefined ||
            this.listAlias === null
        ) {
            return false;
        }

        if (
            !this.isOfType
        ) {
            return false;
        }
        return true;
    }

    isCalcField(selectedValue: string): boolean {
        return selectedValue === FormFlowFieldTypes.Calc;
    }

    validate(fieldName: string, fieldValue: string) {
        if (fieldName === 'Field Id') {
            if (!fieldValue) {
                this.errorString = 'Field Id cannot be blank.';
                return false;
            }
            const fieldValueUpper = fieldValue.toUpperCase();
            const compareLetter = new RegExp('^[0-9]');
            const matchNum = fieldValue.match(compareLetter);

            if (matchNum && matchNum.length >= 0) {
                this.errorString = 'Field Id cannot start with a number.';
                return false;
            }

            if (fieldValueUpper.startsWith('XML')) {
                this.errorString = 'Field Id cannot start with xml.';
                return false;
            }

            if (this.moduleService.isFieldIdUsed(fieldValueUpper)) {
                this.errorString =
                    'Field ID cannot match an existing field ID in the same formflow or a core Ava Field.';
                return false;
            }

            if (this.doesFieldIdConflictWithGroupableField(fieldValueUpper)) {
                this.errorString =
                    'Field ID cannot match an existing groupable field.';
                return false;
            }

            if (this.locationAttributeUsesFieldId(fieldValueUpper)) {
                this.errorString =
                    'Field ID cannot match an existing field ID of a hierarchy attribute.';
                return false;
            }

            if (fieldValueUpper.trim() === 'YEAR') {
                this.errorString =
                    '"Year" is reserved and cannot be used.';
                return false;
            }

            if (!fieldValue || fieldValue.length <= 0) {
                this.errorString = 'Field Id cannot be blank.';
                return false;
            }

            const regex = new RegExp('^[a-zA-Z0-9_]*$');

            if (!regex.test(fieldValue)) {
                this.errorString =
                    'Field Id may only contain alpha-numeric characters and underscores';
                return false;
            }
        } else if (
            fieldName === 'Label' &&
            (!fieldValue || fieldValue.length <= 0)
        ) {
            this.errorString = 'Label cannot be blank.';
            return false;
        }

        this.errorString = null;
        this.isDisabled = false;
        return true;
    }

    getFileTypeDisplayName(fileType: FileTypes): string {
        return FileTypeDescriptions.get(fileType);
    }

    public isMandatoryShouldNotBeSelectableForFieldType(): boolean {
        switch (this.selectedValue) {
            case FormFlowFieldTypes.Escalation:
            case FormFlowFieldTypes.Label:
                return true;
            default:
                return false;
        }
    }

    updateFieldId(value: string): void {
        this.fieldId$.next(value);
    }

    onMultilineFieldChange(): void {
        if (!this.fieldForm.IsMultiLine) {
            this.fieldForm.IsMarkdown = false;
        }
    }

    updateSimpleUserListPermissionsFilter(filterByPermissions: boolean): void {
        this.fieldForm.SimpleUserListSelectedModule = null;
        this.fieldForm.SimpleUserListSelectedPermission = null;

        this.simpleUserListPermissionsFilter = filterByPermissions;
        this.fieldForm.SimpleUserListPermissionsFilter = filterByPermissions;
        this.fieldForm.IsOfType = this.isOfType;
        this.fieldForm.ListType = this.isOfType;

        if (this.simpleUserListPermissionsFilter) {
            this.selectedModule = this.moduleId;
            this.fieldForm.SimpleUserListSelectedModule = this.moduleId;
            this.selectedPermission = this.getPermissionsForModule(this.selectedModule)[0];
            this.fieldForm.SimpleUserListSelectedPermission = this.selectedPermission.Name;
        }
    }

    doesFieldIdConflictWithGroupableField(fieldId: string): boolean {
        for (const groupable of this.groupableFields ?? []) {
            if (groupable.GroupByProperty?.toUpperCase() === fieldId) {
                return true;
            }
        }
        return false;
    }

    setSelectedModule(moduleId: string): void {
        this.fieldForm.SimpleUserListSelectedModule = moduleId;
    }

    setSelectedPermission(permission: PermissionViewModel): void {
        this.fieldForm.SimpleUserListSelectedPermission = permission.Name;
    }

    private locationAttributeUsesFieldId(fieldValueUpper: string): boolean {
        return this.locationAttributes.some(locationAttribute =>
            locationAttribute.AttributeId.toUpperCase() === fieldValueUpper ||
            locationAttribute.AttributeName.toUpperCase() === fieldValueUpper);
    }

    private getAllMatricesAndMapMatrixNames() {
        this.tenantService.getAllMatrices().subscribe((result) => {
            this.matrixNames = result.map(m => m.Name);
        });
    }

    private subscribeToFieldId(): void {
        this.fieldId$
            .pipe(takeUntil(this.destroy$), debounceTime(600), distinctUntilChanged())
            .subscribe((fieldId) => {
                this.validate('Field Id', fieldId);
            });
    }

    private escalationApplicableModuleStatusIdsFieldValidation(): boolean {
        if (
            this.selectedValue === FormFlowFieldTypes.Escalation &&
            (!this.fieldForm.EscalationApplicableModuleStatusIds ||
                this.fieldForm.EscalationApplicableModuleStatusIds.length <= 0)
        ) {
            this.applicableModuleStatusesError =
                'At least one module status must be selected.';
            return false;
        }

        this.applicableModuleStatusesError = '';
        return true;
    }

    private simpleUserListValidation(): boolean {
        if (
            this.selectedValue === FormFlowFieldTypes.SimpleUserList &&
            !this.simpleUserListIsOfTypes.includes(this.isOfType)
        ) {
            this.simpleUserListErrorMessage =
                'Please select a List Type.';
            return false;
        }

        this.simpleUserListErrorMessage = '';
        return true;
    }
}